/**
 * What one `{{ … }}` placeholder asks for: a path into the host's values, and
 * the text to show when that path prints nothing.
 */
export type PlaceholderExpression = {
  /**
   * Property names to follow from the root of the values, in order. An array
   * position is its index as a string with leading zeros dropped — `list[0]`,
   * `list[00]` and `list.0` all read key `"0"` — because every step is an
   * own-property lookup by the time it runs.
   */
  path: string[];
  /** Everything after `??`, trimmed. Absent when the placeholder has no `??`. */
  fallback?: string;
};

/**
 * A key, then any number of `.key` or `[index]` steps, then optionally `??`
 * and fallback text. Spaces are allowed around the path and nowhere inside it.
 *
 * Keys are ASCII letters, digits, `_` and `-`, which covers camelCase,
 * snake_case and kebab-case JSON. Anything else between the braces makes the
 * whole token literal text, so an author who mistypes sees braces rather than
 * a silently empty gap.
 */
const EXPRESSION = /^\s*([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+|\[\d+\])*)\s*(?:\?\?([\s\S]*))?$/;

/** One step of a path that `EXPRESSION` has already accepted. */
const PATH_STEP = /\[(\d+)\]|([A-Za-z0-9_-]+)/g;

/**
 * The inside of one placeholder token — the text between `{{` and `}}` — to
 * the path and fallback it names, or `undefined` when it is not a placeholder
 * after all and should be shown exactly as written.
 *
 * The grammar is specified in `RText.specs.md` under "Placeholders". Every
 * port of the text component implements the same one, so a change here is a
 * change to all of them.
 */
export function parsePlaceholderExpression(inner: string): PlaceholderExpression | undefined {
  const match = EXPRESSION.exec(inner);
  if (!match) {
    return undefined;
  }

  const path: string[] = [];
  for (const [, index, key] of match[1].matchAll(PATH_STEP)) {
    path.push(index === undefined ? key : String(Number.parseInt(index, 10)));
  }

  return match[2] === undefined ? { path } : { path, fallback: match[2].trim() };
}
